/**
 * `JournalSegment.publicText` backport -- matching a parsed Discord dream group to a DB segment
 * (PURE module; no store/DB/React imports, so `Match.test.ts` runs in the Node lane).
 *
 * The parser (`DiscordExport.ts`) says *what* was posted. This says *where it goes*: which
 * `JournalEntry` (ie. which night) and which `JournalSegment` within it (ie. which dream of that
 * night) the post describes.
 *
 * ## The two-sided model
 *
 * A "night" in the DB is one `JournalEntry`. Its `segments` array holds dream segments interleaved
 * with "wake marker" segments (those have a non-null `wakeTime`). Dream segments are classified by
 * the two booleans the DB already has:
 *
 * | group kind | DB segment                                   |
 * |------------|----------------------------------------------|
 * | `lucid`    | `lucid == true`                              |
 * | `nearLucid`| `semiLucid == true` (and not `lucid`)        |
 * | `nonLucid` | neither                                      |
 *
 * The N-th `# First lucid` / `# Second lucid` post of a night is therefore the N-th *lucid* segment of
 * that entry -- which is why the parser resolves ordinals per kind, and why `# Near-lucid` sitting
 * between `# First lucid` and `# Second lucid` does not shift the lucid numbering.
 *
 * ## The date
 *
 * A post's date (`DreamGroup.dreamDate`) is the MORNING the dream ended on. The entry-side
 * equivalent is its latest wake timestamp (falling back to `sleepTime + 8h`), formatted at the SAME
 * UTC offset as the post. Both sides are epoch milliseconds, so using one offset for both is
 * self-consistent regardless of what the machine's actual timezone is.
 *
 * Dates are compared as whole-day differences and scored rather than filtered hard, because:
 *  - backlog posts legitimately reference a dream several days earlier (the explicit date is parsed,
 *    so those land exactly), and
 *  - the fallback "post timestamp" date can be off by one when a log is written the next morning.
 *
 * ## Output
 *
 * A suggestion is NEVER authoritative -- the review UI shows it, shows the runner-up
 * `alternates`, and lets the user pick anything else, because a wrong-but-plausible match is exactly
 * the kind of error that must not be written silently into the journal.
 */
import {DreamDate, DreamGroup, DreamGroupKind, FormatDreamDate, GetLocalDateParts, ToDayNumber} from "./DiscordExport.js";
import {ASSUMED_SLEEP_DURATION_MS, EstimateJournalEntryEndTime_Fallback} from "../Server/DB/@Shared/JournalTimeEstimates.js";

// constants
// ==========

/** How many days apart a post's dream-date and an entry's wake-date may be and still be considered
 * the same night. 2, because a log written a day late legitimately drifts by one. */
export const DEFAULT_MAX_DAY_DIFF = 2;
/** How many alternative segments the review UI is given alongside the top suggestion. */
export const DEFAULT_MAX_ALTERNATES = 12;
// `ASSUMED_SLEEP_DURATION_MS` now lives with the shared estimates (`Server/DB/@Shared/EntryTimeEstimates.ts`),
// and is re-exported here so this module's surface is unchanged.
export {ASSUMED_SLEEP_DURATION_MS};

/** Weights behind `SegmentRef.score`. Higher = more likely to be the post's segment; the relative
 * gaps are what matter (a day off costs more than a kind match is worth). */
export const MatchScore = {
	base: 100,
	/** subtracted per day of date drift */
	perDayOff: 30,
	kindMatch: 40,
	ordinalMatch: 20,
	/** slight preference for a segment that has not been imported into yet */
	emptyTarget: 5,
	/** two DIFFERENT entries closer than this in score are reported `ambiguous` rather than guessed */
	ambiguousMargin: 25,
};

/** Placeholder date for an entry with no usable timestamp: still selectable, but offered last. */
const UNKNOWN_ENTRY_DATE: DreamDate = {year: 0, month: 1, day: 1};

// input shapes (structurally minimal, so `JournalEntry`/`JournalSegment` are assignable)
// ==========

export type JournalSegmentForImport = {
	id?: string|n;
	wakeTime?: number|n;
	lucid?: boolean|n;
	semiLucid?: boolean|n;
	publicText?: string|n;
	shortText?: string|n;
	longText?: string|n;
};

export type JournalEntryForImport = {
	id: string;
	title?: string|n;
	createdAt?: number|n;
	sleepTime?: number|n;
	wakeTime?: number|n;
	segments: Array<JournalSegmentForImport|n>;
};

export type MatchOptions = {
	/** minutes EAST of UTC; MUST match the value used by the parser */
	utcOffsetMinutes?: number;
	/** how many days of drift to still consider (default `DEFAULT_MAX_DAY_DIFF`) */
	maxDayDiff?: number;
	/** how many alternates to return (default `DEFAULT_MAX_ALTERNATES`) */
	maxAlternates?: number;
};

// output shapes
// ==========

export type SegmentRef = {
	entryId: string;
	entryTitle: string;
	/** index within `entry.segments` (wake markers included, so it matches the DB array) */
	segmentIndex: number;
	segmentId: string|n;
	kind: DreamGroupKind;
	/** 1-based index among the entry's segments of the same kind */
	ordinalWithinKind: number;
	dreamDate: DreamDate|n;
	/** entry dream date minus group dream date, in days */
	dayDiff: number;
	score: number;
	/** human-readable, eg. `2026-10-05 · seg 2 · lucid · 812 chars` */
	label: string;
	existingPublicTextLength: number;
};

export const MatchWarning_values = [
	"noCandidate", // nothing within maxDayDiff
	"dateOffByNDays", // matched, but the dates are not identical
	"kindMismatch", // matched segment is not the post's lucidity kind
	"ordinalMismatch", // matched segment is not the post's ordinal within that kind
	"noSegmentOfKind", // the entry has no segment of the post's kind at all
	"targetHasPublicText", // the suggested segment already has publicText -- do not overwrite blindly
	"ambiguous", // two different entries scored similarly
] as const;
export type MatchWarning = typeof MatchWarning_values[number];

export type GroupMatch = {
	target: SegmentRef|n;
	alternates: SegmentRef[];
	/** the entry the target belongs to (convenience for the UI's entry dropdown) */
	entryIdsInOrder: string[];
	warnings: MatchWarning[];
	/** `target.score` when a target exists, else 0 */
	score: number;
};

// segment/entry helpers
// ==========

/** How the DB stores this segment's lucidity, in the same vocabulary the posts use. */
export function GetSegmentKind(segment: JournalSegmentForImport|n): DreamGroupKind {
	if (segment == null) return "nonLucid";
	if (segment.lucid) return "lucid";
	if (segment.semiLucid) return "nearLucid";
	return "nonLucid";
}

/** Dream segments only -- wake markers describe the gap between dreams, not a dream. */
export function IsDreamSegment(segment: JournalSegmentForImport|n) {
	return segment != null && segment.wakeTime == null;
}

/** The "morning date" of an entry, ie. what the author would call the dream's date: the night's end
 * (`EstimateDreamEndTime_Fallback`, so a sparsely-recorded night still gets one) read as a calendar date at
 * a fixed UTC offset -- a parameter, since the Discord side must be interpreted at the same offset. */
export function GetEntryDreamDate(entry: JournalEntryForImport, utcOffsetMinutes = 0): DreamDate|n {
	const endTime = EstimateJournalEntryEndTime_Fallback(entry);
	if (endTime == null) return null;
	const local = GetLocalDateParts(endTime, utcOffsetMinutes);
	return {year: local.year, month: local.month, day: local.day};
}

export function GetSegmentKindOrdinal(entry: JournalEntryForImport, segmentIndex: number): number {
	let ordinal = 0;
	for (let i = 0; i <= segmentIndex; i++) {
		const segment = entry.segments[i];
		if (!IsDreamSegment(segment)) continue;
		if (GetSegmentKind(segment) == GetSegmentKind(entry.segments[segmentIndex])) ordinal++;
	}
	return ordinal;
}

/** Every dream segment of one entry, labelled the way the review UI's segment dropdown shows them. */
export function GetDreamSegmentsOfEntry(entry: JournalEntryForImport, utcOffsetMinutes = 0) {
	const entryDate = GetEntryDreamDate(entry, utcOffsetMinutes);
	const countsByKind = new Map<DreamGroupKind, number>();
	const result: SegmentRef[] = [];
	for (let i = 0; i < entry.segments.length; i++) {
		const segment = entry.segments[i];
		if (!IsDreamSegment(segment)) continue;
		const kind = GetSegmentKind(segment);
		const ordinalWithinKind = (countsByKind.get(kind) ?? 0) + 1;
		countsByKind.set(kind, ordinalWithinKind);
		const existingPublicTextLength = (segment!.publicText ?? "").trim().length;
		result.push({
			entryId: entry.id,
			entryTitle: entry.title ?? "",
			segmentIndex: i,
			segmentId: segment!.id ?? null,
			kind,
			ordinalWithinKind,
			dreamDate: entryDate,
			dayDiff: 0,
			score: 0,
			label: BuildSegmentLabel(entry, entryDate, i, kind, existingPublicTextLength),
			existingPublicTextLength,
		});
	}
	return result;
}

/** Entries whose night date is within `maxDayDiff` days of the group's date, nearest first.
 * The UI offers these as the manual "entry" choice, using a wider window than the automatic
 * suggestion (a backlog post can reference a night the scoring would reject). */
export function GetCandidateEntries(group: DreamGroup, entries: JournalEntryForImport[], options: MatchOptions = {}) {
	const utcOffsetMinutes = options.utcOffsetMinutes ?? 0;
	const maxDayDiff = options.maxDayDiff ?? DEFAULT_MAX_DAY_DIFF;
	const groupDay = group.dreamDate == null ? null : ToDayNumber(group.dreamDate);

	const result: Array<{entry: JournalEntryForImport, dreamDate: DreamDate, dayDiff: number, dreamSegmentCount: number}> = [];
	for (const entry of entries) {
		if (entry?.id == null) continue;
		const dreamDate = GetEntryDreamDate(entry, utcOffsetMinutes);
		const dreamSegmentCount = entry.segments.filter(a=>IsDreamSegment(a)).length;
		if (dreamDate == null) {
			// an entry with no usable timestamp can only be offered as a last resort
			result.push({entry, dreamDate: UNKNOWN_ENTRY_DATE, dayDiff: Number.MAX_SAFE_INTEGER, dreamSegmentCount});
			continue;
		}
		if (groupDay == null) {
			result.push({entry, dreamDate, dayDiff: Number.MAX_SAFE_INTEGER, dreamSegmentCount});
			continue;
		}
		const dayDiff = ToDayNumber(dreamDate) - groupDay;
		if (Math.abs(dayDiff) > maxDayDiff) continue;
		result.push({entry, dreamDate, dayDiff, dreamSegmentCount});
	}
	result.sort((a,b)=>Math.abs(a.dayDiff) - Math.abs(b.dayDiff));
	return result;
}

// matching
// ==========

/** Scores every dream segment of every entry within `maxDayDiff` days of the group's date, and picks
 * the best one. Never throws, never assumes a match exists. */
export function MatchGroupToSegment(group: DreamGroup, entries: JournalEntryForImport[], options: MatchOptions = {}): GroupMatch {
	const utcOffsetMinutes = options.utcOffsetMinutes ?? 0;
	const maxDayDiff = options.maxDayDiff ?? DEFAULT_MAX_DAY_DIFF;
	const maxAlternates = options.maxAlternates ?? DEFAULT_MAX_ALTERNATES;

	const groupDay = group.dreamDate == null ? null : ToDayNumber(group.dreamDate);
	const candidates: SegmentRef[] = [];
	const entryIdsInOrder: string[] = [];
	const kindCountsPerEntry = new Map<string, Map<DreamGroupKind, number>>();

	for (const entry of entries) {
		if (entry?.id == null) continue;
		const entryDate = GetEntryDreamDate(entry, utcOffsetMinutes);
		if (entryDate == null || groupDay == null) continue;
		const dayDiff = ToDayNumber(entryDate) - groupDay;
		if (Math.abs(dayDiff) > maxDayDiff) continue;

		const counts = new Map<DreamGroupKind, number>();
		for (let i = 0; i < entry.segments.length; i++) {
			const segment = entry.segments[i];
			if (!IsDreamSegment(segment)) continue;
			const kind = GetSegmentKind(segment);
			const ordinalWithinKind = (counts.get(kind) ?? 0) + 1;
			counts.set(kind, ordinalWithinKind);

			const existingPublicText = (segment!.publicText ?? "").trim();
			let score = MatchScore.base;
			score -= Math.abs(dayDiff) * MatchScore.perDayOff;
			if (kind == group.kind) score += MatchScore.kindMatch;
			if (ordinalWithinKind == group.ordinal) score += MatchScore.ordinalMatch;
			if (existingPublicText.length == 0) score += MatchScore.emptyTarget;

			candidates.push({
				entryId: entry.id,
				entryTitle: entry.title ?? "",
				segmentIndex: i,
				segmentId: segment!.id ?? null,
				kind,
				ordinalWithinKind,
				dreamDate: entryDate,
				dayDiff,
				score,
				label: BuildSegmentLabel(entry, entryDate, i, kind, existingPublicText.length),
				existingPublicTextLength: existingPublicText.length,
			});
		}
		kindCountsPerEntry.set(entry.id, counts);
		if (counts.size > 0) entryIdsInOrder.push(entry.id);
	}

	candidates.sort((a,b)=>b.score - a.score);
	const target = candidates[0] ?? null;
	const alternates = candidates.slice(1, 1 + maxAlternates);

	const warnings: MatchWarning[] = [];
	if (target == null) {
		warnings.push("noCandidate");
	} else {
		if (target.dayDiff != 0) warnings.push("dateOffByNDays");
		if (target.kind != group.kind) warnings.push("kindMismatch");
		if (target.kind == group.kind && target.ordinalWithinKind != group.ordinal) warnings.push("ordinalMismatch");
		if (!kindCountsPerEntry.get(target.entryId)?.get(group.kind)) warnings.push("noSegmentOfKind");
		if (target.existingPublicTextLength > 0) warnings.push("targetHasPublicText");

		// two different entries scoring similarly means the date alone cannot decide it
		const runnerUp = alternates.find(a=>a.entryId != target.entryId);
		if (runnerUp != null && target.score - runnerUp.score < MatchScore.ambiguousMargin) warnings.push("ambiguous");
	}

	return {target, alternates, entryIdsInOrder, warnings, score: target?.score ?? 0};
}

function BuildSegmentLabel(entry: JournalEntryForImport, entryDate: DreamDate|n, segmentIndex: number, kind: DreamGroupKind, existingLength: number) {
	const parts = [
		FormatDreamDate(entryDate),
		`seg ${segmentIndex}`,
		kind,
		existingLength > 0 ? `${existingLength} chars filled` : "empty",
	];
	const title = (entry.title ?? "").trim();
	if (title.length > 0) parts.push(title);
	return parts.join(" · ");
}

/** A draft can be applied in one click when the match is unambiguous AND would not overwrite text. */
export function IsMatchConfident(match: GroupMatch) {
	if (match.target == null) return false;
	if (match.warnings.includes("targetHasPublicText")) return false;
	if (match.warnings.includes("ambiguous")) return false;
	if (match.warnings.includes("kindMismatch")) return false;
	if (match.warnings.includes("ordinalMismatch")) return false;
	if (match.warnings.includes("dateOffByNDays")) return false;
	return true;
}

// existing-text comparison
// ==========

/** How much of an incoming `publicText` is ALREADY in the target segment. Distinguishes "this import
 * was applied before" (eg. a batch that was interrupted and then re-started) from a genuine conflict
 * where the segment holds something else. */
export type PublicTextOverlap = {
	incomingLines: number;
	matchedLines: number;
	existingLines: number;
	/** lines the segment has that the incoming text does not (0 when the import wrote all of them) */
	extraExistingLines: number;
	/** every incoming line is already present -> this text has been applied before */
	allMatch: boolean;
};

/** Lines are compared trimmed and blank-insensitive, so trailing whitespace / blank-line differences
 * (which `publicText` round-trips through the DB can introduce) do not count as a mismatch. */
function SplitComparableLines(text: string) {
	return (text ?? "").replace(/\r\n?/g, "\n").split("\n").map(a=>a.trim()).filter(a=>a.length > 0);
}

export function GetPublicTextOverlap(incomingText: string, existingText: string): PublicTextOverlap {
	const incoming = SplitComparableLines(incomingText);
	const existing = SplitComparableLines(existingText);
	// multiset intersection: a line counts as matched only as many times as it occurs in both
	const remaining = new Map<string, number>();
	for (const line of existing) remaining.set(line, (remaining.get(line) ?? 0) + 1);
	let matchedLines = 0;
	for (const line of incoming) {
		const count = remaining.get(line) ?? 0;
		if (count <= 0) continue;
		matchedLines++;
		remaining.set(line, count - 1);
	}
	return {
		incomingLines: incoming.length,
		matchedLines,
		existingLines: existing.length,
		extraExistingLines: existing.length - matchedLines,
		allMatch: incoming.length > 0 && matchedLines == incoming.length,
	};
}
